/** Shell components: header, footer, wordmark, proof badge, glyph */

import type { ProofClass } from "@apf/site-publication";
import type { EditionId } from "../edition.ts";
import { editionPrefix } from "../edition.ts";

// ---------------------------------------------------------------------------
// Wordmark
// ---------------------------------------------------------------------------
export function Wordmark({ edition }: { edition: EditionId }) {
  return (
    <div className="lockup">
      <a href={editionPrefix(edition) + "/"} aria-label="WORKABLE home">
        <img
          src="/brand/workable-wordmark-color.svg"
          alt="WORKABLE"
          className="wordmark wordmark-positive"
          width={168}
          height={40}
        />
        <img
          src="/brand/workable-wordmark-white.svg"
          alt="WORKABLE"
          className="wordmark wordmark-negative"
          width={168}
          height={40}
        />
      </a>
      {edition === "accenture" && (
        <span className="edition-label" aria-label="Accenture Edition">Accenture Edition</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Site Shell
// ---------------------------------------------------------------------------
export function Shell({
  edition,
  tagline,
  credit,
  published = true,
  children,
}: {
  edition: EditionId;
  tagline: string;
  credit: string;
  /** False while the edition awaits admission: product links would 404. */
  published?: boolean;
  children: React.ReactNode;
}) {
  const prefix = editionPrefix(edition);
  const switchLabel = edition === "accenture" ? "WORKABLE Core" : "Accenture Edition";
  const switchHref = edition === "accenture" ? "/" : "/accenture-edition";

  return (
    <div data-edition={edition === "accenture" ? "accenture" : undefined}>
      <a href="#main" className="skip">Skip to content</a>
      <header className="site-header">
        <Wordmark edition={edition} />
        <nav className="site-nav" aria-label="Primary">
          {published && <a href={`${prefix}/constructs`} className="nav-secondary">Constructs</a>}
          <a href={switchHref} className="btn">
            {switchLabel}
          </a>
        </nav>
      </header>
      <main id="main">{children}</main>
      <footer className="site-footer">
        <div>
          <Wordmark edition={edition} />
          <p>{tagline}</p>
          <p className="small">{credit}</p>
        </div>
        {published && (
          <div>
            <div className="footer-title">Products</div>
            <a href={`${prefix}/constructs`}>All Constructs</a>
            <a href={`${prefix}/constructs/value-hunter`}>Value Hunter</a>
          </div>
        )}
        <div>
          <div className="footer-title">Editions</div>
          <a href="/">WORKABLE Core</a>
          <a href="/accenture-edition">Accenture Edition</a>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Proof badge
// ---------------------------------------------------------------------------
const PROOF_LABELS: Record<ProofClass, string> = {
  "recipe": "Recipe",
  "illustrative-example": "Illustrative example",
  "demo-workblock": "Demo Workblock",
  "configured-deployment": "Configured deployment",
  "production-evidence": "Production evidence",
};

export function ProofBadge({ proofClass }: { proofClass: ProofClass }) {
  return (
    <span className={`status ${proofClass}`} title={PROOF_LABELS[proofClass]}>
      {PROOF_LABELS[proofClass]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Not found
// ---------------------------------------------------------------------------
export function NotFound({ path }: { path: string }) {
  return (
    <div className="wrap" style={{ paddingTop: 96, paddingBottom: 96 }}>
      <p className="eyebrow">404</p>
      <h1 style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>Page not found.</h1>
      <p className="muted">
        The path <code>{path}</code> doesn't exist.
      </p>
      <a href="/" className="btn" style={{ marginTop: 24 }}>Go to WORKABLE Core</a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Awaiting publication (pins.json has no admitted releases yet)
// ---------------------------------------------------------------------------
export function AwaitingPublication({ edition }: { edition: EditionId }) {
  const name = edition === "accenture" ? "WORKABLE, Accenture Edition" : "WORKABLE Core";
  return (
    <div className="wrap" style={{ paddingTop: 96, paddingBottom: 96 }} data-testid="awaiting-publication">
      <p className="eyebrow">{name}</p>
      <h1 style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>Publishing soon.</h1>
      <p className="muted" style={{ maxWidth: 560 }}>
        This edition is published from admitted product releases. It will appear here once they are released.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Glyph (Blockwork glyph SVG wrapper)
// ---------------------------------------------------------------------------
export function Glyph({ name, label }: { name: string; label: string }) {
  // Glyphs are served from /brand/glyphs/<name>.svg
  return (
    <span className="glyph" aria-label={label} role="img">
      <img
        src={`/brand/glyphs/${name}.svg`}
        alt=""
        width={24}
        height={24}
        style={{ display: "block", width: "100%", height: "100%" }}
      />
    </span>
  );
}
